import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { authApi, meApi } from '../api/auth';
import { isUnauthorized, setUnauthorizedHandler } from '../api/client';
import type { AppRole, UserResponse } from '../api/types';
import { AuthContext, type AuthContextValue } from './AuthContext';
import { returnTo } from './returnTo';
import { tokenStorage } from './tokenStorage';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  // Сессия протухла где-то в фоновом запросе — убираем пользователя из состояния,
  // чтобы защищённые маршруты сразу увели на страницу входа.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (tokenStorage.getRefresh() === null) {
        setIsRestoring(false);
        return;
      }

      try {
        // Если access-токен просрочен, перехватчик сам обновит его по refresh.
        // Сколько бы человек не заходил, пароль спрашивать не нужно: refresh
        // бессрочный, пока пользователь не вышел сам.
        const profile = await meApi.get();
        if (!cancelled) {
          setUser(profile);
        }
      } catch (error) {
        // Токен стираем, только если сервер его отверг. Недоступный сервер
        // сессию не отменяет — она восстановится при следующей загрузке.
        if (isUnauthorized(error)) {
          tokenStorage.clear();
        }
      } finally {
        if (!cancelled) {
          setIsRestoring(false);
        }
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const auth = await authApi.login(email, password);
    tokenStorage.save(auth.accessToken, auth.refreshToken);
    setUser(auth.user);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStorage.getRefresh();

    if (refreshToken !== null) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // Даже если сервер не ответил, локальную сессию всё равно закрываем.
      }
    }

    tokenStorage.clear();
    // Явный выход отменяет и прежнюю цель: следующий вход начинается с кабинета,
    // а не с оплаты, за которой человек приходил до выхода. Заодно запрещаем
    // запомнить страницу, с которой выходим: без этого защита маршрута успела
    // бы записать её заново на перерисовке без пользователя.
    returnTo.forget(window.location.pathname + window.location.search + window.location.hash);
    returnTo.clear();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isRestoring,
      isAuthenticated: user !== null,
      hasRole: (role: AppRole) => user?.roles.includes(role) ?? false,
      login,
      logout,
      setUser,
    }),
    [user, isRestoring, login, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
